if (!customElements.get('size-chart-tabs')) {
  customElements.define(
    'size-chart-tabs',
    class SizeChartTabs extends HTMLElement {
      constructor() {
        super();
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.selectTab(tab));
          tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, tab));
        });

        this.initUnitToggles();
        this.initScrollableTables();
      }

      initScrollableTables() {
        const tables = Array.from(this.querySelectorAll('table'));
        if (!tables.length) return;

        const wrapped = () => tables.every((table) => table.closest('.rte__table-wrapper'));

        this.updateScrollableTables();

        if (!wrapped()) {
          const observer = new MutationObserver(() => {
            this.updateScrollableTables();
            if (wrapped()) observer.disconnect();
          });
          observer.observe(this, { childList: true, subtree: true });
        }

        window.addEventListener('resize', () => this.updateScrollableTables());
      }

      updateScrollableTables() {
        this.querySelectorAll('table').forEach((table) => {
          const wrapper = table.closest('.rte__table-wrapper');
          if (!wrapper) return;

          const isScrollable = wrapper.scrollWidth > wrapper.clientWidth;
          if (isScrollable) {
            wrapper.setAttribute('tabindex', '0');
            wrapper.setAttribute('role', 'region');
            wrapper.setAttribute(
              'aria-label',
              table.querySelector('caption')?.textContent.trim() || 'Scrollable table'
            );
          } else {
            wrapper.removeAttribute('tabindex');
            wrapper.removeAttribute('role');
            wrapper.removeAttribute('aria-label');
          }
        });
      }

      handleTabKeydown(event, currentTab) {
        const currentIndex = this.tabs.indexOf(currentTab);
        const targetIndex = {
          ArrowRight: (currentIndex + 1) % this.tabs.length,
          ArrowLeft: (currentIndex - 1 + this.tabs.length) % this.tabs.length,
          Home: 0,
          End: this.tabs.length - 1,
        }[event.key];

        if (targetIndex === undefined) return;

        event.preventDefault();
        const nextTab = this.tabs[targetIndex];
        nextTab.focus();
        this.selectTab(nextTab);
      }

      selectTab(selectedTab) {
        this.tabs.forEach((tab) => {
          const isSelected = tab === selectedTab;
          tab.classList.toggle('is-active', isSelected);
          tab.setAttribute('aria-selected', isSelected);
          tab.setAttribute('tabindex', isSelected ? '0' : '-1');
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.id !== selectedTab.getAttribute('aria-controls');
        });

        this.updateScrollableTables();
      }

      initUnitToggles() {
        this.querySelectorAll('[data-unit-toggle]').forEach((toggle) => {
          const panel = toggle.closest('[role="tabpanel"]');
          if (!panel) return;

          const cells = Array.from(panel.querySelectorAll('table td, table th'));
          cells.forEach((cell) => {
            const inches = this.parseInches(cell.textContent);
            if (inches === null) return;
            cell.dataset.inches = cell.textContent.trim();
            cell.dataset.cm = (inches * 2.54).toFixed(1);
          });

          toggle.querySelectorAll('[data-unit]').forEach((button) => {
            button.addEventListener('click', () => this.setUnit(toggle, cells, button.dataset.unit));
          });
        });
      }

      parseInches(text) {
        const trimmed = (text || '').trim();
        const match = trimmed.match(/^(\d+(?:\.\d+)?)?\s*(?:(\d+)\/(\d+))?$/);
        if (!match || (!match[1] && !match[2])) return null;
        const whole = match[1] ? parseFloat(match[1]) : 0;
        const numerator = match[2] ? parseInt(match[2], 10) : 0;
        const denominator = match[3] ? parseInt(match[3], 10) : 1;
        return whole + numerator / denominator;
      }

      setUnit(toggle, cells, unit) {
        cells.forEach((cell) => {
          if (!cell.dataset.inches) return;
          cell.textContent = unit === 'cm' ? cell.dataset.cm : cell.dataset.inches;
        });

        toggle.querySelectorAll('[data-unit]').forEach((button) => {
          const isActive = button.dataset.unit === unit;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive);
        });

        const label = toggle.querySelector('[data-unit-label]');
        if (label) {
          label.textContent = unit === 'cm' ? 'Showing centimeters' : 'Showing inches';
        }
      }
    }
  );
}